import { useMemo } from 'react';
import { useTranslation } from '@shared/core';
import type { IEventSubTypeConfiguration, IWorkspaceTile } from '../hooks/useEventSubTypeConfig';

const DEFAULT_TILE_ITEMS: readonly IWorkspaceTile[] = [
  {
    componentId: 'event_countdown',
    route: '/edit/settings',
  },
  {
    componentId: 'concierge',
    link: 'https://withjoy.typeform.com/to/fT5TU4VL',
  },
  { componentId: 'website' },
];

export const useWeddingDefaultConfig = (): IEventSubTypeConfiguration => {
  const { tq } = useTranslation('eventWorkspace');

  return useMemo<IEventSubTypeConfiguration>(
    () => ({
      category: 'wedding',
      categorySubType: 'default',
      title: 'Wedding',
      description: 'Event template for wedding events.',
      insights: ['Additional AI friendly descriptors here to help AI have deeper context into what this event type and data structure is about'],
      location: {
        city: 'San Francisco',
        state: 'CA',
        country: 'US',
      },
      date: 'ISO 8601 date with timezone offset here',
      eventImage: 'https://withjoy.com/assets/public/proto/modern/corpor',
      eventIcon: '',
      apps: [
        {
          appId: 'website',
          label: tq`Website`,
          route: '/edit/design',
          color: {
            start: 'rgba(82, 146, 241, 1)',
            end: 'rgba(38, 100, 194, 1)',
          },
          description: tq`Design and customize your event website`,
          keywords: ['website', 'design', 'customize', 'event'],
          iconUrl: 'https://withjoy.com/assets/public/proto/modern/website.png',
        },
        {
          label: tq`Registry`,
          route: '/edit/registry',
          color: {
            start: 'rgba(255, 111, 159, 1)',
            end: 'rgba(189, 0, 63, 1)',
          },
          description: tq`Manage your wedding gift registry and cash funds with ease`,
          keywords: ['registry', 'gifts', 'wedding', 'cash', 'fund'],
          iconUrl: 'https://withjoy.com/assets/public/proto/modern/registry.png',
        },
        {
          label: tq`Guest Stays`,
          route: '/edit/accommodations',
          color: {
            start: 'rgba(217, 140, 255, 1)',
            end: 'rgba(82, 41, 164, 1)',
          },
          description: tq`Simplify guest accommodations while saving them money`,
          keywords: ['guest', 'stays', 'accommodations', 'hotels', 'travel'],
          iconUrl: 'https://withjoy.com/assets/public/proto/modern/stays.png',
        },
        {
          label: tq`Contacts`,
          route: '/edit/contact',
          color: {
            start: 'rgba(255, 225, 155, 1)',
            end: 'rgba(255, 208, 98, 1)',
          },
          description: tq`Collect and manage contacts for your event`,
          keywords: ['contact', 'collect', 'manage', 'event', 'guest', 'address', 'email', 'phone'],
          iconUrl: 'https://withjoy.com/assets/public/proto/modern/contact_collector.png',
        },
        {
          appId: 'guest_list',
          label: tq`Guest List`,
          link: '${eventHandle}/edit/guests',
          color: {
            start: 'rgba(255, 225, 155, 1)',
            end: 'rgba(255, 208, 98, 1)',
          },
          description: tq`Manage the guests attending your event, including RSVPs, dietary restrictions, and more`,
          keywords: ['guest', 'list', 'manage', 'rsvp', 'dietary', 'restrictions'],
          iconUrl: 'https://withjoy.com/assets/public/proto/modern/guest_list.png',
        },
        {
          label: tq`Messaging`,
          route: '/edit/messaging',
          color: {
            start: 'rgba(138, 255, 152, 1)',
            end: 'rgba(0, 205, 24, 1)',
          },
          description: tq`Communicate with your guests through messaging`,
          keywords: ['email', 'sms', 'messaging', 'communication', 'guest', 'notify', 'remind'],
          iconUrl: 'https://withjoy.com/assets/public/proto/modern/messaging.png',
        },
        {
          appId: 'event_schedule',
          label: tq`Schedule`,
          link: '${eventHandle}/edit/schedule',
          color: {
            start: 'rgba(255, 225, 155, 1)',
            end: 'rgba(255, 208, 98, 1)',
          },
          description: tq`Event schedule to define times and locations for various activities during the event`,
          keywords: ['event', 'schedule', 'agenda', 'itinerary', 'times', 'dates', 'locations'],
          iconUrl: 'https://withjoy.com/assets/public/proto/modern/schedule.png',
        },
        {
          label: tq`Save the Date`,
          route: '/edit/cards/wedding/save-the-date',
          color: {
            start: 'rgba(140, 255, 217, 1)',
            end: 'rgba(25, 183, 131, 1)',
          },
          description: tq`Create and send a Save the Date for your wedding`,
          keywords: [],
          iconUrl: 'https://withjoy.com/assets/public/proto/modern/save_the_date.png',
        },
        {
          label: tq`RSVP`,
          link: '${eventHandle}/edit/rsvp',
          color: {
            start: 'rgba(255, 211, 36, 1)',
            end: 'rgba(255, 211, 36, 1)',
          },
          description: tq`Manage your event RSVPs, including guest responses and dietary restrictions`,
          keywords: ['rsvp', 'manage', 'guest', 'responses', 'dietary', 'restrictions'],
          iconUrl: 'https://withjoy.com/assets/public/proto/modern/rsvp.png',
        },
        {
          label: tq`Invitation`,
          route: '/edit/cards/wedding/invitation',
          color: {
            start: 'rgba(140, 255, 217, 1)',
            end: 'rgba(25, 183, 131, 1)',
          },
          description: tq`Design and send invitations for your wedding`,
          keywords: ['wedding', 'invitation', 'design', 'send'],
          iconUrl: 'https://withjoy.com/assets/public/proto/modern/invitations.png',
        },
        {
          label: tq`Day of`,
          route: '/edit/cards/wedding/menu',
          color: {
            start: 'rgba(140, 255, 217, 1)',
            end: 'rgba(25, 183, 131, 1)',
          },
          description: tq`Browse signs, menus, and other stationery for your wedding`,
          keywords: ['stationery', 'signs', 'menus', 'wedding'],
          iconUrl: 'https://withjoy.com/assets/public/proto/modern/stationery.png',
        },
        {
          label: tq`Photos`,
          route: '/edit/website/photos',
          color: {
            start: 'rgba(255, 173, 137, 1)',
            end: 'rgba(255, 77, 0, 1)',
          },
          description: tq`Manage your event photos and albums`,
          keywords: ['photos', 'albums', 'gallery', 'images'],
          iconUrl: 'https://withjoy.com/assets/public/proto/modern/moments.png',
        },
        {
          appId: 'settings',
          label: tq`Settings`,
          route: '/edit/settings',
          color: {
            start: 'rgba(215, 220, 226, 1)',
            end: 'rgba(110, 113, 119, 1)',
          },
          background: 'linear-gradient(140.91deg, #6C6B6B 1.75%, #202020 93.35%)',
          description: tq`Event settings to manage your event details, including title, date, location, and more`,
          keywords: ['settings', 'date', 'location', 'configure', 'manage'],
          iconUrl: 'https://withjoy.com/assets/public/pro/icons/settings_v1.svg',
        },
      ],
      tiles: DEFAULT_TILE_ITEMS,
    }),
    [tq],
  );
};
